import "./Dashboard.css";

import { useLocation } from "react-router-dom";

import { OrganisationRoleConstants, ResourcePermissionConstants } from "../../../constants";
import { IStateOrigin } from "../../../navigators/navigateStateObjects";
import { DeveloperTemplate } from "../../../templates";
import { VersionConflictModal } from "../../../templates/DeveloperProjectCardWithTabsTemplate/components";
import {
  getActivitiesRoute,
  // getMarketingRoute,  // TODO ENG-557 hidden until post-launch
  getProjectsRoute,
} from "../../../utils/routes";
import {
  Column,
  InformationBox,
  KanaTooltip,
  ProfileMenu,
  ProfileMenuItem,
  QuickActionsMenu,
  Row,
  UnitsKPI,
  UserHeader,
} from "../../../widget";
import { DashboardPieCard } from "../../shared/components";
import {
  CreateProjectModal,
  // DashboardBarCard,  // TODO ENG-557 hidden until post-launch
  DashboardTableCard,
} from "./components";
import { useDashboard } from "./useDashboard";

const Dashboard = (): JSX.Element => {
  const {
    developerHasReceivedProjectInvitations,
    developerReceivedProjectInvitationType,
    navigateToProjectInvitations,
    phaseChartData,
    // metricsChartData, // TODO ENG-557 hidden until post-launch
    tableColumnsTopProjects,
    tableColumnsInProgressActivities,
    tableColumnsInReviewActivities,
    isLoading,
    isLoadingProjects,
    isLoadingActivities,
    onTopProjectsDataLoad,
    onRecentActivitiesInProgressDataLoad,
    onRecentActivitiesChangesRequiredDataLoad,
    quickActionsMenuOptionsList,
    showProfileMenu,
    showCreateProjectModal,
    setShowProfileMenu,
    setShowCreateProjectModal,
    setShowVersionConflictModal,
    showVersionConflictModal,
    hasPermission,
    newVersionActivityHistory,
    draftActivityHistoryUuid,
    currentUserType,
    currentUserRole,
    unitsKey,
    onSelectChange,
    dropDownData,
  } = useDashboard();
  // const marketingRoute = getMarketingRoute();  // TODO ENG-557 hidden until post-launch
  const location = useLocation();
  const invitationType = developerReceivedProjectInvitationType?.toLowerCase();
  return (
    <DeveloperTemplate activeElement="Dashboard">
      <div className="Dashboard">
        <Row spacingV="ml">
          <Column span={12}>
            <UserHeader />
          </Column>
        </Row>
        {developerHasReceivedProjectInvitations &&
          developerReceivedProjectInvitationType &&
          (currentUserRole === OrganisationRoleConstants.OWNER ||
            currentUserRole === OrganisationRoleConstants.ADMIN) && (
            <Row spacingV="l">
              <Column span={12}>
                <InformationBox variant="info">
                  <div className="ProjectInvitationNotificationContent">
                    <p className="body2">
                      You have received a project access {invitationType}, please view your organisation&apos;s{" "}
                      {invitationType}s to accept or decline.
                    </p>
                    <button className="BtnLink" onClick={navigateToProjectInvitations}>
                      View {invitationType}s
                    </button>
                  </div>
                </InformationBox>
              </Column>
            </Row>
          )}
        <Row spacingV="ml">
          <Column span={12}>
            <div className="HeaderContainer">
              <h1>Dashboard</h1>
              {hasPermission(ResourcePermissionConstants.CREATE_RESOURCE) && (
                <div>
                  <QuickActionsMenu onClick={() => setShowProfileMenu(true)} />
                  <ProfileMenu show={showProfileMenu} onClose={() => setShowProfileMenu(false)} position={{ top: "0" }}>
                    {quickActionsMenuOptionsList.map((el) => (
                      <ProfileMenuItem
                        key={el.id}
                        profileMenuItem={el}
                        onClick={() => {
                          setShowCreateProjectModal(true);
                          setShowProfileMenu(false);
                        }}
                      />
                    ))}
                  </ProfileMenu>
                </div>
              )}
            </div>
          </Column>
        </Row>

        {/* Loading state --------- */}
        {isLoading && (
          <Row spacingV="ml">
            <Column span={6}>
              <DashboardPieCard
                title="Project phase"
                data={{}}
                tooltip={
                  <KanaTooltip
                    tooltipHeader="Project Phase:"
                    tooltipText="Shows the number of projects by phases of their workflow."
                  />
                }
                isGradient
                centerTextFont="Fraunces"
                colours="roadmap"
              />
            </Column>
            <Column span={6}>
              <div className="DLoadingCard">
                <p className="body01">Units</p>
                <p className="body2 NoData">Loading...</p>
              </div>
            </Column>
          </Row>
        )}

        {!isLoading && (
          <Row spacingV="ml">
            <Column span={6}>
              <DashboardPieCard
                title="Project phase"
                data={phaseChartData}
                tooltip={
                  <KanaTooltip
                    tooltipHeader="Project phase:"
                    tooltipText="Shows the number of projects by phases of their workflow."
                  />
                }
                isGradient
                centerTextFont="Fraunces"
                colours="roadmap"
              />
            </Column>
            <Column span={6}>
              <UnitsKPI
                unitsKey={unitsKey}
                dropdownOptions={dropDownData}
                showTooltip
                title="Inventory"
                onSelectChange={onSelectChange}
              />
            </Column>
          </Row>
        )}
        {/* TODO ENG-557 hidden until post-launch */}
        {/* <Row spacingV="ml">
              <Column span={12}>
                <DashboardBarCard
                  title="Marketing page views"
                  expandRoute={marketingRoute}
                  data={metricsChartData}
                />
              </Column>
            </Row> */}
        <Row spacingV="ml">
          <Column span={12}>
            <DashboardTableCard
              title="Activities in progress"
              expandRoute={getActivitiesRoute(currentUserType).concat("/in_progress")}
              state={{ origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin }}
              onChange={onRecentActivitiesInProgressDataLoad}
              columns={tableColumnsInProgressActivities}
              isLoading={isLoadingActivities}
            />
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={12}>
            <DashboardTableCard
              title="Activities in review"
              expandRoute={getActivitiesRoute(currentUserType).concat("/in_review")}
              state={{ origin: { goBackText: "Back to dashboard", from: location.pathname } as IStateOrigin }}
              onChange={onRecentActivitiesChangesRequiredDataLoad}
              columns={tableColumnsInReviewActivities}
              isLoading={isLoadingActivities}
            />
          </Column>
        </Row>
        <Row spacingV="ml">
          <Column span={12}>
            <DashboardTableCard
              title="Top projects"
              expandRoute={getProjectsRoute(currentUserType)}
              onChange={onTopProjectsDataLoad}
              columns={tableColumnsTopProjects}
              isLoading={isLoadingProjects}
            />
          </Column>
        </Row>
      </div>
      <CreateProjectModal
        showCreateProjectModal={showCreateProjectModal}
        closeModal={() => setShowCreateProjectModal(false)}
      />
      <VersionConflictModal
        showVersionConflictModal={showVersionConflictModal}
        draftActivityHistoryUuid={draftActivityHistoryUuid}
        closeModal={() => setShowVersionConflictModal(false)}
        newVersionActivityHistory={newVersionActivityHistory}
      />
    </DeveloperTemplate>
  );
};

export default Dashboard;
